import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_URL } from '../../api';

const FILE_TYPE_COLORS = {
    pdf: 'bg-red-700 text-white',
    html: 'bg-blue-700 text-white',
    epub: 'bg-purple-700 text-white',
    image: 'bg-green-700 text-white',
};
const fileTypeColor = (ft) => FILE_TYPE_COLORS[ft?.toLowerCase()] || 'bg-gray-700 text-white';

const TRADITIONS = ['All', 'Kagyu', 'Nyingma', 'Gelug', 'Sakya', 'Zen', 'Theravada', 'Vajrayana', 'Other'];
const FILE_TYPES = ['All', 'PDF', 'HTML', 'EPUB', 'Image', 'Other'];

const LibraryWorks = () => {
    const navigate = useNavigate();
    const [works, setWorks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tradition, setTradition] = useState('All');
    const [fileType, setFileType] = useState('All');

    const fetchWorks = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (tradition !== 'All') params.set('tradition', tradition);
            if (fileType !== 'All') params.set('fileType', fileType.toLowerCase());
            params.set('limit', '30');

            const res = await fetch(`${API_URL}/library/works?${params.toString()}`);
            if (!res.ok) throw new Error(`Request failed (${res.status})`);
            const data = await res.json();
            setWorks(data.works || []);
        } catch (err) {
            setError('Unable to load dharma works at this time.');
        } finally {
            setIsLoading(false);
        }
    }, [tradition, fileType]);

    useEffect(() => {
        fetchWorks();
    }, [fetchWorks]);

    return (
        <div className="min-h-screen py-12 px-6">
            <div className="max-w-6xl mx-auto">
                {/* Breadcrumb */}
                <nav className="mb-6 text-sm text-gray-400" aria-label="Breadcrumb">
                    <Link to="/library" className="hover:text-amber-400 transition-colors">Library</Link>
                    <span className="mx-2 text-gray-600">&rsaquo;</span>
                    <span className="text-gray-200">Dharma Works</span>
                </nav>

                {/* Header */}
                <div className="mb-8">
                    <div className="inline-block bg-slate-900/70 backdrop-blur-sm rounded-xl px-8 py-6">
                        <h1 className="text-3xl font-bold text-white mb-1">Dharma Works</h1>
                        <p className="text-gray-300 text-sm">
                            PDFs, texts, and teachings from across Buddhist traditions
                        </p>
                    </div>
                </div>

                {/* Filter bar */}
                <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-4 mb-8 flex flex-wrap gap-4 items-center">
                    <div className="flex items-center gap-2">
                        <label htmlFor="tradition-filter" className="text-gray-300 text-sm font-medium whitespace-nowrap">
                            Tradition
                        </label>
                        <select
                            id="tradition-filter"
                            value={tradition}
                            onChange={(e) => setTradition(e.target.value)}
                            className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-amber-500"
                        >
                            {TRADITIONS.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <label htmlFor="filetype-filter" className="text-gray-300 text-sm font-medium whitespace-nowrap">
                            File Type
                        </label>
                        <select
                            id="filetype-filter"
                            value={fileType}
                            onChange={(e) => setFileType(e.target.value)}
                            className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-amber-500"
                        >
                            {FILE_TYPES.map((ft) => (
                                <option key={ft} value={ft}>{ft}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Loading state */}
                {isLoading && (
                    <div className="flex justify-center py-20">
                        <div className="w-10 h-10 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" aria-label="Loading" />
                    </div>
                )}

                {/* Error state */}
                {!isLoading && error && (
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-8 text-center">
                        <p className="text-gray-400">{error}</p>
                        <button
                            onClick={fetchWorks}
                            className="mt-4 text-amber-400 hover:underline text-sm"
                        >
                            Try again
                        </button>
                    </div>
                )}

                {/* Empty state */}
                {!isLoading && !error && works.length === 0 && (
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-12 text-center">
                        <svg className="w-12 h-12 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                        </svg>
                        <p className="text-gray-400 text-lg">No works found</p>
                        <p className="text-gray-500 text-sm mt-1">Try adjusting your filters</p>
                    </div>
                )}

                {/* Works grid */}
                {!isLoading && !error && works.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {works.map((work) => (
                            <div
                                key={work.id || work.workId}
                                className="bg-slate-900/70 backdrop-blur-sm border border-slate-700 hover:border-amber-500/50 rounded-xl p-5 flex flex-col gap-3 transition-colors"
                            >
                                <div>
                                    <h2 className="text-white font-semibold leading-snug mb-1">{work.title}</h2>
                                    {work.author && (
                                        <p className="text-gray-400 text-sm">{work.author}</p>
                                    )}
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {work.tradition && (
                                        <span className="text-xs bg-slate-700 text-gray-300 px-2 py-0.5 rounded-full">
                                            {work.tradition}
                                        </span>
                                    )}
                                    {work.language && (
                                        <span className="text-xs bg-slate-700 text-gray-300 px-2 py-0.5 rounded-full">
                                            {work.language}
                                        </span>
                                    )}
                                    {work.fileType && (
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${fileTypeColor(work.fileType)}`}>
                                            {work.fileType.toUpperCase()}
                                        </span>
                                    )}
                                </div>

                                {work.description && (
                                    <p className="text-gray-400 text-sm leading-relaxed line-clamp-2">
                                        {work.description}
                                    </p>
                                )}

                                <div className="mt-auto pt-2">
                                    <button
                                        onClick={() => navigate(`/library/works/${work.id || work.workId}`)}
                                        className="text-sm bg-amber-600 hover:bg-amber-500 text-white px-4 py-1.5 rounded-full font-medium transition-colors"
                                    >
                                        View
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LibraryWorks;
